import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderMathContent } from '../utils/renderMath';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { loadExamSession } from '../data/examData';
import { generateKoreanExamTitle, getKoreanExamName } from '../utils/examNameMapping';
import { getEnglishKey } from '../utils/koreanNameMapper';
import { saveWrongAnswer } from '../utils/wrongAnswerStorage';
import { useAuth } from '../contexts/AuthContext';
import { updateQuestionProgress } from '../utils/learningProgressStorage';

// 구 버전 데이터 로딩 함수들 제거됨 - 트리 구조 시스템으로 대체

// 선택지 구조 정규화 함수
function normalizeOptions(options) {
  if (!options || !Array.isArray(options)) return [];
  
  return options.map(option => {
    // 이미 객체 형태인 경우
    if (typeof option === 'object' && option !== null) {
      return {
        text: option.text || option.value || '',
        ...option
      };
    }
    // 문자열인 경우 객체로 변환
    if (typeof option === 'string') {
      return {
        text: option
      };
    }
    // 그 외의 경우 빈 객체 반환
    return {
      text: String(option || '')
    };
  });
}

// 범용적 이미지 경로 생성 함수
function getImagePath(examType, year, session, imageSrc) {
  // 이미지 src에서 'images/' 접두사 제거 (JSON에서 일부에 포함됨)
  const cleanImageSrc = imageSrc.replace(/^images\//, '');
  
  // 실제 폴더 구조에 맞는 경로 패턴 (우선순위순)
  const possiblePaths = [
    // 1. 정확한 실제 폴더 구조 (영문 examType + session-X 형식)
    `/images/${examType}/${year}/session-${session}/${cleanImageSrc}`,
    
    // 2. 대체 경로들 (fallback)
    `/images/${examType}/${year}/session-${session}/${imageSrc}`,
    `/images/${examType}/${year}/${session}/${cleanImageSrc}`,
    `/images/${examType}/${year}/${session}/${imageSrc}`,
    
    // 3. 단순 경로
    `/images/${cleanImageSrc}`,
    `/images/${imageSrc}`,
    
    // 4. 기존 파일 구조들
    `/src/data/images/${cleanImageSrc}`,
    `/data/images/${cleanImageSrc}`,
    
    // 5. 절대 경로
    cleanImageSrc.startsWith('/') ? cleanImageSrc : `/${cleanImageSrc}`,
    imageSrc.startsWith('/') ? imageSrc : `/${imageSrc}`
  ];
  
  // 첫 번째 경로를 기본으로 반환 (onError에서 fallback 처리)
  return possiblePaths[0];
}

export default function CBT() {
  const { examSlug, year, session } = useParams();
  const navigate = useNavigate();
  
  // URL 파라미터를 examType으로 변환 (한글 URL 지원)
  const rawExamSlug = examSlug || 'electrical-engineer';
  // 한글 자격증명인 경우 영문 키로 변환 (예: "전기기능사" → "electrical-technician")
  const examType = getEnglishKey(decodeURIComponent(rawExamSlug)) || rawExamSlug;
  const safeExamType = examType;
  const safeYear = parseInt(year) || 2022;
  const safeSession = parseInt(session) || 1;
  const safeStartQuestion = 0; // 0-based index로 변환

  const [current, setCurrent] = useState(safeStartQuestion);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(false);
  const [time, setTime] = useState(180 * 60); // 180 minutes in seconds
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAIExplanation, setShowAIExplanation] = useState(false);
  const [showResults, setShowResults] = useState({}); // 각 문제별 정답 표시 상태
  const [isAnswerLocked, setIsAnswerLocked] = useState({}); // 답변 잠금 상태
  const [currentStreak, setCurrentStreak] = useState(0); // 현재 연승
  
  const { user } = useAuth(); // 사용자 정보 가져오기

  // 한글 자격증명 가져오기 (중앙화된 매핑 시스템 사용)
  const getKoreanExamType = (type) => {
    if (!type) return '전기기사'; // 기본값
    return getKoreanExamName(type);
  };

  // Helper functions
  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const score = () => {
    if (!data || !data.questions) return 0;
    let correct = 0;
    let totalAnswered = 0;
    
    data.questions.forEach(q => {
      if (answers[q.id] !== undefined) {
        totalAnswered++;
        if (answers[q.id] === q.correctAnswer) correct++;
      }
    });
    
    // 전체 문제 대비 정답률 계산
    return Math.round((correct / data.questions.length) * 100);
  };
  
  const getCorrectCount = () => {
    if (!data || !data.questions) return 0;
    return data.questions.filter(q => answers[q.id] === q.correctAnswer).length;
  };
  
  const getWrongCount = () => {
    if (!data || !data.questions) return 0;
    return data.questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== q.correctAnswer).length;
  };

  useEffect(() => {
    if (time > 0 && !result) {
      const timer = setInterval(() => setTime(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (time === 0 && !result) {
      setResult(true);
    }
  }, [time, result]);


  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle keyboard navigation if not typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (current > 0) {
            setCurrent(c => Math.max(0, c - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (current < data?.questions?.length - 1) {
            setCurrent(c => c + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (current === data?.questions?.length - 1) {
            // Last question - submit exam
            if (window.confirm(`총 ${data.questions.length}문제 중 ${getAnsweredCount()}문제에 답변하셨습니다.\n\n정말 시험을 제출하시겠습니까?`)) {
              setResult(true);
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [current, data, getAnsweredCount]);

  // Dynamic data loading effect
  useEffect(() => {
    const loadExamData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load exam session data from tree structure
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 트리 구조에서 시험 데이터 로딩: ${safeExamType}/${safeYear}/session-${safeSession}`);
        }
        const examData = await loadExamSession(safeExamType, safeYear, safeSession);
        
        if (!examData) {
          throw new Error(`시험 데이터를 찾을 수 없습니다: ${safeExamType} ${safeYear}년 ${safeSession}회`);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ 트리 구조 시험 데이터 로딩 성공: ${examData.questions?.length || 0}문제`);
        }
        
        // 트리 구조 데이터 처리 및 정규화
        const processedExamData = {
          examInfo: {
            title: generateKoreanExamTitle(
              safeExamType, 
              safeYear, 
              safeSession, 
              examData.examInfo?.title
            ),
            subject: examData.examInfo?.subject || safeExamType,
            timeLimit: examData.examInfo?.timeLimit || 180,
            passingScore: examData.examInfo?.passingScore || 60
          },
          questions: (examData.questions || []).map(q => ({
            ...q,
            options: normalizeOptions(q.options), // 선택지 구조 정규화
            image: q.image || null,
            // 이미지 경로 처리 (트리 구조에서 생성된 imagePath 사용)
            imagePath: q.imagePath || (q.image ? `/images/${safeExamType}/${safeYear}/session-${safeSession}/${q.image}` : null)
          }))
        };
        
        // 시험 시간 설정
        setTime(processedExamData.examInfo.timeLimit * 60);
        
        // 최종 데이터 설정
        setData(processedExamData);
        
      } catch (err) {
        console.error('Failed to load exam data:', err);
        setError(`시험 데이터를 불러올 수 없습니다: ${safeExamType} ${safeYear}년 ${safeSession}회차\n오류: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadExamData();
  }, [safeExamType, safeYear, safeSession]);

  // 한글 자격증명으로 document title 설정
  useEffect(() => {
    if (data && data.examInfo) {
      const koreanTitle = getKoreanExamType(safeExamType);
      document.title = `${koreanTitle} ${safeYear}년 ${safeSession}회차 - 기출문제 CBT`;
    }
  }, [data, safeExamType, safeYear, safeSession]);

  // 세션 데이터 관리 및 페이지 종료 시 데이터 저장
  useEffect(() => {
    const userId = user?.id || 'guest';
    const sessionStartTime = Date.now();
    
    // 페이지 언마운트 또는 새로고침 시 세션 데이터 저장
    const handleBeforeUnload = () => {
      if (data?.questions && getAnsweredCount() > 0) {
        const sessionTime = Math.round((Date.now() - sessionStartTime) / 1000 / 60); // 분 단위
        const correctAnswers = getCorrectCount();
        const totalAnswered = getAnsweredCount();
        
        const { updateSessionComplete } = require('../utils/learningProgressStorage');
        updateSessionComplete(userId, {
          sessionTime,
          questionsAnswered: totalAnswered,
          correctAnswers,
          examType: `${safeExamType}_${safeYear}_${safeSession}`,
          completedExam: false // 중간에 나간 경우
        });
      }
    };
    
    // 컴포넌트 언마운트 시에도 데이터 저장
    const handleUnload = () => {
      handleBeforeUnload();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup function - 컴포넌트 언마운트 시 실행
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleUnload();
    };
  }, [data, answers, user?.id, safeExamType, safeYear, safeSession]);

  // LaTeX 수식 렌더링 함수
  // 수식 렌더링: 공용 유틸 사용 (src/utils/renderMath.jsx)

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">시험 데이터 로딩 중...</h2>
          <p className="text-gray-600">{getKoreanExamType(safeExamType)} {safeYear}년 {safeSession}회차 문제를 준비하고 있습니다.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">데이터 로딩 실패</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!data || !data.questions || data.questions.length === 0) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-gray-600 mb-2">문제 데이터 없음</h2>
          <p className="text-gray-600">선택한 시험의 문제 데이터가 없습니다.</p>
        </div>
      </div>
    );
  }

  if (result) {
    const finalScore = score();
    const passingScore = 60;
    const answeredCount = getAnsweredCount();
    
    return (
      <div className="min-h-screen bg-white text-gray-900 relative overflow-hidden">
        <div className="relative z-20 min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 py-8">
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-orange-200">
              <div className="mb-8">
                <div className="text-7xl font-bold mb-6 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">{finalScore}점</div>
                <div className={`text-3xl font-bold ${finalScore >= passingScore ? 'text-green-600' : 'text-red-600'}`}>
                  {finalScore >= passingScore ? '🎉 합격' : '😢 불합격'}
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-2xl p-8 mb-8 border border-orange-200">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <span className="text-gray-600">총 문제 수:</span>
                  <span className="font-semibold ml-2">{data.questions.length}문제</span>
                </div>
                <div>
                  <span className="text-gray-600">답변한 문제:</span>
                  <span className="font-semibold ml-2">{answeredCount}문제</span>
                </div>
                <div>
                  <span className="text-gray-600">정답 수:</span>
                  <span className="font-semibold ml-2 text-green-600">
                    {Math.round((finalScore * data.questions.length) / 100)}문제
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">오답 수:</span>
                  <span className="font-semibold ml-2 text-red-600">
                    {answeredCount - Math.round((finalScore * data.questions.length) / 100)}문제
                  </span>
                </div>
              </div>
            </div>
            
              <button 
                onClick={() => window.location.reload()} 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-12 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg"
              >
                다시 시작하기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const q = data.questions[current];
  const progress = ((current + 1) / data.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-primary-50 text-gray-900 relative grid grid-rows-[auto_1fr_auto]">
      {/* Side Navigation Arrows */}
      {current > 0 && (
        <button
          onClick={() => {
            setCurrent(c => Math.max(0, c - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="fixed left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-30 
                     bg-white/90 backdrop-blur-sm hover:bg-white/95 
                     w-11 h-11 md:w-12 md:h-12 rounded-full shadow-lg hover:shadow-xl
                     flex items-center justify-center
                     transition-all duration-300 hover:scale-110 active:scale-95
                     border border-gray-200 hover:border-orange-300
                     touch-manipulation"
          aria-label="이전 문제"
          title="이전 문제 (← 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {current < data.questions.length - 1 ? (
        <button
          onClick={() => {
            setCurrent(c => c + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="fixed right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-30 
                     bg-white/90 backdrop-blur-sm hover:bg-white/95 
                     w-11 h-11 md:w-12 md:h-12 rounded-full shadow-lg hover:shadow-xl
                     flex items-center justify-center
                     transition-all duration-300 hover:scale-110 active:scale-95
                     border border-gray-200 hover:border-orange-300
                     touch-manipulation"
          aria-label="다음 문제"
          title="다음 문제 (→ 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : (
        <button
          onClick={() => {
            if (window.confirm(`총 ${data.questions.length}문제 중 ${getAnsweredCount()}문제에 답변하셨습니다.\n\n정말 시험을 제출하시겠습니까?`)) {
              setResult(true);
            }
          }}
          className="fixed right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-30 
                     bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700
                     w-11 h-11 md:w-12 md:h-12 rounded-full shadow-lg hover:shadow-xl
                     flex items-center justify-center
                     transition-all duration-300 hover:scale-110 active:scale-95
                     border border-green-400
                     touch-manipulation"
          aria-label="시험 제출"
          title="시험 제출 (Enter 키)"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}

      <div className="relative z-20 h-full grid grid-rows-[auto_1fr_auto]">
      {/* Header - Fixed */}
      <header className="bg-gradient-to-r from-orange-50 to-orange-100 shadow-sm border-b border-orange-200 px-4 py-3">
        <div className="max-w-6xl mx-auto relative">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div>
                <h1 className="text-lg font-bold text-primary-600">{data.examInfo.title}</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`text-xl font-mono font-bold ${time < 600 ? 'text-red-500' : 'text-gray-800'}`}>
                {formatTime(time)}
              </div>
              {/* 종료 버튼 - X 아이콘 */}
              <button 
                onClick={() => {
                  if (window.confirm('정말 시험을 종료하시겠습니까? 진행 상황은 저장됩니다.')) {
                    navigate(`/${examSlug}`);
                  }
                }}
                className="p-1 hover:opacity-80 transition-all duration-200 group"
                title="시험 종료"
              >
                <svg className="w-7 h-7 text-gray-500 hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-orange-500 to-orange-600 h-1.5 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>진행률: {Math.round(progress)}%</span>
              <span>답변: {getAnsweredCount()} / {data.questions.length}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content Area - Scrollable */}
      <main className="overflow-y-auto">
        <div className="max-w-6xl mx-auto p-4">
          <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-lg p-4 mb-4 border border-white/50">
          
          {/* 문제 번호 - 좌측 상단에 크게 걸치게 표시 */}
          <div className="absolute -top-3 -left-1 text-orange-500 font-normal text-lg">
            Q{q.questionNumber}
          </div>
          
          {/* 문제 텍스트 - 바로 시작 */}
          <div className="text-lg leading-relaxed mb-4 text-gray-800 whitespace-pre-wrap">
            {typeof q.questionText === 'string' 
              ? renderMathContent(q.questionText)
              : q.questionText
            }
          </div>
          
          {/* Image if exists */}
          {q.image && q.image.src && (
            <div className="mb-3">
              <img 
                src={getImagePath(safeExamType, safeYear, safeSession, q.image.src)}
                alt={q.image.description || "문제 관련 이미지"}
                className="w-auto h-auto max-w-full max-h-56 sm:max-h-72 md:max-h-80 object-contain"
                onError={(e) => {
                  // 강화된 폴백 메커니즘 - getImagePath와 동일한 로직 사용
                  const currentSrc = e.target.src;
                  const cleanImageSrc = q.image.src.replace(/^images\//, '');
                  
                  const possiblePaths = [
                    // 1. 정확한 실제 폴더 구조 (영문 examType + session-X 형식)
                    `/images/${safeExamType}/${safeYear}/session-${safeSession}/${cleanImageSrc}`,
                    
                    // 2. 대체 경로들 (fallback)
                    `/images/${safeExamType}/${safeYear}/session-${safeSession}/${q.image.src}`,
                    `/images/${safeExamType}/${safeYear}/${safeSession}/${cleanImageSrc}`,
                    `/images/${safeExamType}/${safeYear}/${safeSession}/${q.image.src}`,
                    
                    // 3. 단순 경로
                    `/images/${cleanImageSrc}`,
                    `/images/${q.image.src}`,
                    
                    // 4. 기존 파일 구조들
                    `/src/data/images/${cleanImageSrc}`,
                    `/data/images/${q.image.src}`,
                    
                    // 5. 절대 경로
                    cleanImageSrc.startsWith('/') ? cleanImageSrc : `/${cleanImageSrc}`,
                    q.image.src.startsWith('/') ? q.image.src : `/${q.image.src}`
                  ];
                  
                  // 현재 시도한 경로를 찾아서 다음 경로 시도
                  const currentIndex = possiblePaths.findIndex(path => currentSrc.endsWith(path.replace(/^\//, '')));
                  const nextIndex = currentIndex + 1;
                  
                  if (nextIndex < possiblePaths.length) {
                    console.warn(`이미지 로드 실패, 다음 경로 시도: ${possiblePaths[nextIndex]}`);
                    e.target.src = possiblePaths[nextIndex];
                  } else {
                    console.warn('모든 이미지 경로 시도 실패:', q.image.src);
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }
                }}
              />
              <div style={{display: 'none'}} className="text-orange-600 text-sm mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <span className="font-medium">📷 이미지를 불러올 수 없습니다</span>
                <br />
                <span className="text-xs text-gray-500">파일: {q.image.src}</span>
                <br />
                <span className="text-xs text-gray-400">시험: {safeExamType} {safeYear}년 {safeSession}회차</span>
              </div>
            </div>
          )}
          
          {/* Options */}
          <div className="space-y-2">
            {q.options && q.options.length > 0 ? (
              q.options.map((option, index) => {
                const optionNumber = index + 1;
                const isSelected = answers[q.id] === optionNumber;
                
                // 선택지 텍스트 추출 (객체 또는 문자열 모두 처리)
                const optionText = typeof option === 'object' && option !== null && option.text 
                  ? option.text 
                  : typeof option === 'string' 
                    ? option 
                    : '';
                
                // 이 문제가 이미 답변되었는지 확인
                const isQuestionAnswered = showResults[q.id];
                const isCorrectOption = q.correctAnswer === optionNumber;
                const isWrongSelected = isQuestionAnswered && isSelected && !isCorrectOption;
                
                return (
                <button 
                  key={`${q.id}-option-${optionNumber}`}
                  onClick={() => {
                    // 이미 답변한 문제는 변경 불가
                    if (isAnswerLocked[q.id]) return;
                    
                    // 답변 선택
                    setAnswers({...answers, [q.id]: optionNumber});
                    
                    // 즉시 정답 공개
                    setTimeout(() => {
                      setShowResults({...showResults, [q.id]: true});
                      setIsAnswerLocked({...isAnswerLocked, [q.id]: true});
                      
                      // 사용자 ID 먼저 정의
                      const userId = user?.id || 'guest';
                      
                      // 연승 업데이트 및 효과
                      if (q.correctAnswer === optionNumber) {
                        // 정답 시 연승 증가
                        setCurrentStreak(prev => prev + 1);
                      } else {
                        // 오답 시 연승 리셋 및 해설 자동 표시
                        setCurrentStreak(0);
                        setShowAIExplanation(true);
                        
                        // 오답 저장
                        const wrongAnswerData = {
                          questionId: q.id,
                          questionText: q.question,
                          options: normalizeOptions(q.options),
                          correctAnswer: q.correctAnswer,
                          userAnswer: optionNumber,
                          explanation: q.explanation || '해설이 없습니다.',
                          examType: safeExamType,
                          subject: q.subject || '기타',
                          difficulty: q.difficulty || 'medium',
                          image: q.image,
                          tags: q.tags || [],
                          year: safeYear,
                          session: safeSession,
                          questionNumber: current + 1 // 문제 번호 추가 (1부터 시작)
                        };
                        
                        saveWrongAnswer(userId, wrongAnswerData);
                      }
                      
                      // 학습 진도 업데이트 (정답/오답 모두)
                      const progressData = {
                        examType: `${safeExamType}_${safeYear}_${safeSession}`,
                        subject: q.subject || '기타',
                        isCorrect: q.correctAnswer === optionNumber,
                        timeSpent: 30, // 평균 문제 풀이 시간 (실제로는 타이머로 측정 가능)
                        sessionId: `cbt_${Date.now()}`
                      };
                      
                      updateQuestionProgress(userId, progressData);
                    }, 300); // 300ms 딜레이 후 결과 표시
                  }}
                  disabled={isAnswerLocked[q.id]}
                  className={`
                    relative w-full text-left p-3 pl-5 border-2 rounded-lg 
                    transition-all duration-300 transform-gpu
                    ${
                      isQuestionAnswered
                        ? isCorrectOption
                          ? 'border-green-500 bg-green-50 shadow-lg'
                          : isWrongSelected
                          ? 'border-red-500 bg-red-50 shadow-lg animate-shake'
                          : 'border-gray-300 bg-gray-100 opacity-60'
                        : isSelected 
                          ? 'border-orange-500 bg-orange-50 shadow-lg scale-100' 
                          : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-orange-400 hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
                    }
                    ${isAnswerLocked[q.id] ? 'cursor-not-allowed' : 'cursor-pointer'}
                    touch-manipulation
                  `}
                >
                  {/* 선택지 번호 배지 - 왼쪽 밖으로 걸치게 */}
                  <div className={`
                    absolute -left-3 top-1/2 -translate-y-1/2 
                    flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-md
                    ${
                      isQuestionAnswered && isCorrectOption 
                        ? 'bg-green-500 text-white' 
                        : isQuestionAnswered && isWrongSelected
                        ? 'bg-red-500 text-white'
                        : isSelected
                        ? 'bg-orange-500 text-white'
                        : 'bg-white border-2 border-gray-300 text-gray-600'
                    }
                  `}>
                    {optionNumber}
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* 선택지 텍스트 */}
                    <span className={`flex-1 leading-relaxed ${
                      isQuestionAnswered && isCorrectOption 
                        ? 'text-green-800 font-semibold' 
                        : isQuestionAnswered && isWrongSelected
                        ? 'text-red-800 line-through'
                        : 'text-gray-800'
                    }`}>
                      {typeof optionText === 'string' 
                        ? renderMathContent(optionText)
                        : optionText
                      }
                    </span>
                    {/* 정답/오답 아이콘 표시 */}
                    {isQuestionAnswered && (
                      <div className="ml-2">
                        {isCorrectOption && (
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {isWrongSelected && (
                          <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                </button>
                );
              })
            ) : (
              <div className="text-gray-500 text-center py-4">
                선택지가 없습니다.
              </div>
            )}
          </div>
          
          {/* 정답 피드백 패널 - 정답일 때만 표시 */}
          {showResults[q.id] && answers[q.id] === q.correctAnswer && (
            <div className="mt-3 p-3 rounded-lg border-l-4 animate-fade-in bg-green-50 border-green-500">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold text-green-800 text-lg">정답입니다! 🎉</span>
                </div>
                <span className="text-sm text-gray-600">
                  정답: {q.correctAnswer}번
                </span>
              </div>
              
              {/* 격려 메시지 */}
              <div className="text-sm mb-3">
                <p className="text-green-700">훌륭합니다! 정확하게 이해하고 계시네요.</p>
              </div>
            </div>
          )}
          
          {/* AI 해설 패널 */}
          {(showAIExplanation || showResults[q.id]) && (
            <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-l-4 border-purple-500">
              <div className="flex items-center space-x-2 mb-3">
                <SparklesIcon className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-purple-800">AI 해설</span>
              </div>
              <div className="text-sm text-purple-700 leading-relaxed">
                {q.explanation ? (
                  <div className="whitespace-pre-line">
                    {renderMathContent(q.explanation)}
                  </div>
                ) : (
                  <p className="text-purple-600">
                    💡 이 문제에 대한 해설이 준비 중입니다.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      </main>

      {/* Footer - Status and Quick Navigation */}
      <footer className="bg-white/80 backdrop-blur-md border-t border-white/30 px-4 py-4">
        <div className="max-w-6xl mx-auto">

          {/* Quick Navigation */}
          <div className="bg-white/60 backdrop-blur-sm border border-white/50 rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-semibold text-gray-800">빠른 이동</h3>
              <div className="flex gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-600 rounded"></div>
                  <span>현재</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded"></div>
                  <span>정답</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded"></div>
                  <span>오답</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gray-300 rounded"></div>
                  <span>미답변</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-10 gap-1">
              {data.questions.map((question, index) => {
                const isAnswered = answers[question.id] !== undefined;
                const isRevealed = showResults[question.id];
                const isCorrect = isRevealed && answers[question.id] === question.correctAnswer;
                const isWrong = isRevealed && answers[question.id] !== question.correctAnswer;
                
                return (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrent(index);
                      // 화면을 최상단으로 스크롤
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`w-8 h-8 rounded text-xs font-medium transition-all duration-200 ${
                      index === current
                        ? 'bg-orange-600 text-white ring-2 ring-orange-400'
                        : isCorrect
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : isWrong
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : isAnswered
                        ? 'bg-orange-200 text-orange-700 hover:bg-orange-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={
                      isCorrect ? '정답' : 
                      isWrong ? '오답' : 
                      isAnswered ? '답변함' : 
                      '미답변'
                    }
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </footer>
    </div>
    </div>
  );
}